/**
 * Enhanced EdgeAgentDetector with WASM support
 * Provides both pattern-based and WASM-based detection
 */

import { detectAgentWithWasm, initWasm, isWasmAvailable, setWasmBaseUrl } from './wasm-loader';
import {
  loadRulesSync,
  type DetectionRules,
  type DetectionResult,
  type DetectionInput,
  mapVerificationMethod,
} from '@kya-os/agentshield-shared';

type EventHandler = (...args: unknown[]) => void;

// Load centralized rules - this will fail fast if rules cannot be loaded
const rules: DetectionRules = loadRulesSync();

/**
 * EdgeAgentDetector with WASM enhancement
 */
export class EdgeAgentDetectorWithWasm {
  private wasmEnabled: boolean = false;
  private initPromise: Promise<void> | null = null;
  private baseUrl: string | null = null;
  private rules: DetectionRules;

  constructor(private enableWasm: boolean = true) {
    this.rules = rules;
  }

  /**
   * Set the base URL for WASM loading in Edge Runtime
   */
  setBaseUrl(url: string) {
    this.baseUrl = url;
    setWasmBaseUrl(url);
  }

  /**
   * Initialize the detector (including WASM if enabled)
   */
  async init(): Promise<void> {
    if (!this.enableWasm) {
      this.wasmEnabled = false;
      return;
    }

    if (this.initPromise) {
      await this.initPromise;
      return;
    }

    this.initPromise = (async () => {
      try {
        // Try to initialize WASM
        const wasmAvailable = await isWasmAvailable();

        if (wasmAvailable) {
          // Set base URL if provided before initializing
          if (this.baseUrl) {
            setWasmBaseUrl(this.baseUrl);
          }
          await initWasm(); // Actually initialize WASM
          this.wasmEnabled = true;
          // WASM detection enabled
        } else {
          this.wasmEnabled = false;
          // WASM not available, using pattern detection
        }
      } catch (error) {
        console.error('[AgentShield] Failed to initialize WASM:', error);
        this.wasmEnabled = false;
      }
    })();

    await this.initPromise;
  }

  /**
   * Pattern-based detection (fallback)
   */
  private async patternDetection(input: DetectionInput): Promise<DetectionResult> {
    const reasons: string[] = [];
    let detectedAgent: { type: string; name: string } | undefined;
    let verificationMethod: string | undefined;
    let confidence = 0; // 0-100 scale

    const headers = input.headers || {};
    const normalizedHeaders: Record<string, string> = {};

    // Normalize header names to lowercase
    for (const [key, value] of Object.entries(headers)) {
      normalizedHeaders[key.toLowerCase()] = value;
    }

    // Check for HTTP Message Signatures (highest confidence)
    const signaturePresent = !!(
      normalizedHeaders['signature'] || normalizedHeaders['signature-input']
    );
    const signatureAgent = normalizedHeaders['signature-agent'];

    if (signatureAgent?.includes('chatgpt.com')) {
      confidence = 85; // High confidence for ChatGPT signature
      reasons.push('signature_agent:chatgpt');
      detectedAgent = { type: 'chatgpt', name: 'ChatGPT' };
      verificationMethod = 'signature';
    } else if (signaturePresent) {
      confidence = Math.max(confidence, 40); // Medium confidence for generic signature
      reasons.push('signature_present');
    }

    // Check User-Agent patterns using centralized rules
    const userAgent = input.userAgent || input.headers?.['user-agent'] || '';
    if (userAgent) {
      for (const [agentKey, agentRule] of Object.entries(this.rules.rules.userAgents)) {
        const matched = agentRule.patterns.some((pattern) => {
          const regex = new RegExp(pattern, 'i');
          return regex.test(userAgent);
        });

        if (matched) {
          const agentType = this.getAgentType(agentKey);
          const agentName = this.getAgentName(agentKey);

          confidence = Math.max(confidence, Math.round(agentRule.confidence * 0.85 * 100));
          reasons.push(`known_pattern:${agentType}`);
          if (!detectedAgent) {
            detectedAgent = { type: agentType, name: agentName };
            verificationMethod = 'pattern';
          }
          break;
        }
      }
    }

    // Check AI-specific headers using centralized rules
    const suspiciousHeaders = this.rules.rules.headers.suspicious;
    const foundAiHeaders = suspiciousHeaders.filter(
      (headerRule) => normalizedHeaders[headerRule.name.toLowerCase()]
    );

    if (foundAiHeaders.length > 0) {
      const maxConfidence = Math.max(...foundAiHeaders.map((h) => h.confidence));
      confidence = Math.max(confidence, maxConfidence);
      reasons.push(`ai_headers:${foundAiHeaders.length}`);
    }

    // Check cloud provider IPs using centralized rules
    const ip = input.ip || input.ipAddress;
    if (ip && !normalizedHeaders['x-forwarded-for'] && !normalizedHeaders['x-real-ip']) {
      // Handle both old (with providers) and new (direct) structures
      const ipRanges =
        'providers' in this.rules.rules.ipRanges
          ? this.rules.rules.ipRanges.providers
          : this.rules.rules.ipRanges;

      for (const [provider, ipRule] of Object.entries(ipRanges)) {
        // Skip non-rule entries
        if (
          !ipRule ||
          typeof ipRule !== 'object' ||
          !('ranges' in ipRule) ||
          !Array.isArray(ipRule.ranges)
        )
          continue;

        const matched = ipRule.ranges.some((range: string) => {
          const prefix = range.split('/')[0];
          const prefixParts = prefix.split('.');
          const ipParts = ip.split('.');

          // Check if IP starts with the same octets
          for (let i = 0; i < Math.min(prefixParts.length - 1, 2); i++) {
            if (prefixParts[i] !== ipParts[i] && prefixParts[i] !== '0') {
              return false;
            }
          }
          return true;
        });

        if (matched) {
          confidence = Math.max(confidence, Math.round(ipRule.confidence * 0.4 * 100));
          reasons.push(`cloud_provider:${provider}`);
          break;
        }
      }
    }

    // Boost confidence for combinations
    if (reasons.length > 2) {
      confidence = Math.min(Math.round(confidence * 1.2), 95);
    }

    // Ensure confidence stays within 0-100 range
    confidence = Math.min(Math.max(confidence, 0), 100);

    return {
      isAgent: confidence > 30, // 30% threshold
      confidence,
      detectionClass:
        confidence > 30 && detectedAgent
          ? { type: 'AiAgent' as const, agentType: detectedAgent.name }
          : confidence > 30
            ? { type: 'Unknown' as const }
            : { type: 'Human' as const },
      signals: [], // Will be populated by enhanced detection engine in future tasks
      ...(detectedAgent && { detectedAgent }),
      reasons,
      ...(verificationMethod && {
        verificationMethod: mapVerificationMethod(verificationMethod),
      }),
      forgeabilityRisk: confidence > 80 ? 'medium' : 'high',
      timestamp: new Date(),
    };
  }

  /**
   * Analyze request with WASM enhancement when available
   */
  async analyze(input: DetectionInput): Promise<DetectionResult> {
    // Ensure initialization
    await this.init();

    // Try WASM detection first if enabled
    if (this.wasmEnabled) {
      try {
        const wasmResult = await detectAgentWithWasm(
          input.userAgent || input.headers?.['user-agent'],
          input.headers || {},
          input.ip || input.ipAddress
        );

        if (wasmResult) {
          // WASM detection succeeded

          // Map WASM result to DetectionResult
          const detectedAgent = wasmResult.agent ? this.mapAgentName(wasmResult.agent) : undefined;

          return {
            isAgent: wasmResult.isAgent,
            confidence: wasmResult.confidence,
            detectionClass:
              wasmResult.isAgent && detectedAgent
                ? { type: 'AiAgent' as const, agentType: detectedAgent.name }
                : wasmResult.isAgent
                  ? { type: 'Unknown' as const }
                  : { type: 'Human' as const },
            signals: [], // Will be populated by enhanced detection engine in future tasks
            ...(detectedAgent && { detectedAgent }),
            reasons: [`wasm:${wasmResult.verificationMethod}`],
            verificationMethod: mapVerificationMethod(wasmResult.verificationMethod),
            forgeabilityRisk:
              wasmResult.verificationMethod === 'signature'
                ? 'low'
                : wasmResult.confidence > 90
                  ? 'medium'
                  : 'high',
            timestamp: new Date(),
          };
        }
      } catch (error) {
        console.error('[AgentShield] WASM detection error:', error);
      }
    }

    // Fall back to pattern detection
    const patternResult = await this.patternDetection(input);

    // If WASM is available but didn't detect anything, boost confidence slightly
    // to indicate WASM verification was performed
    if (this.wasmEnabled && patternResult.confidence >= 85) {
      patternResult.confidence = Math.min(95, patternResult.confidence + 10);
      patternResult.reasons.push('wasm_enhanced');
      // Note: Keep original verification method since 'wasm-enhanced' is not a valid type
    }

    return patternResult;
  }

  /**
   * Get agent type from rule key
   */
  private getAgentType(agentKey: string): string {
    const typeMap: Record<string, string> = {
      openai_gptbot: 'openai',
      anthropic_claude: 'anthropic',
      perplexity_bot: 'perplexity',
      google_ai: 'google',
      microsoft_ai: 'microsoft',
      meta_ai: 'meta',
      cohere_bot: 'cohere',
      huggingface_bot: 'huggingface',
      generic_bot: 'generic',
      dev_tools: 'dev',
      automation_tools: 'automation',
    };
    return typeMap[agentKey] || agentKey;
  }

  /**
   * Get agent name from rule key
   */
  private getAgentName(agentKey: string): string {
    const nameMap: Record<string, string> = {
      openai_gptbot: 'ChatGPT/GPTBot',
      anthropic_claude: 'Claude',
      perplexity_bot: 'Perplexity',
      google_ai: 'Google AI',
      microsoft_ai: 'Microsoft Copilot',
      meta_ai: 'Meta AI',
      cohere_bot: 'Cohere',
      huggingface_bot: 'HuggingFace',
      generic_bot: 'Generic Bot',
      dev_tools: 'Development Tool',
      automation_tools: 'Automation Tool',
    };
    return nameMap[agentKey] || agentKey;
  }

  /**
   * Map agent names from WASM to consistent format
   */
  private mapAgentName(agent: string): { type: string; name: string } | undefined {
    const lowerAgent = agent.toLowerCase();

    if (lowerAgent.includes('chatgpt')) {
      return { type: 'chatgpt', name: 'ChatGPT' };
    } else if (lowerAgent.includes('claude')) {
      return { type: 'claude', name: 'Claude' };
    } else if (lowerAgent.includes('perplexity')) {
      return { type: 'perplexity', name: 'Perplexity' };
    } else if (lowerAgent.includes('bing')) {
      return { type: 'bing', name: 'Bing AI' };
    } else if (lowerAgent.includes('anthropic')) {
      return { type: 'anthropic', name: 'Anthropic' };
    }

    return { type: 'unknown', name: agent };
  }
}

/**
 * Wrapper that provides event emitter functionality
 */
export class EdgeAgentDetectorWrapperWithWasm {
  private detector: EdgeAgentDetectorWithWasm;
  private events: Map<string, EventHandler[]> = new Map();

  constructor(config?: { enableWasm?: boolean; baseUrl?: string }) {
    this.detector = new EdgeAgentDetectorWithWasm(config?.enableWasm ?? true);
    if (config?.baseUrl) {
      this.detector.setBaseUrl(config.baseUrl);
    }
  }

  setBaseUrl(url: string) {
    this.detector.setBaseUrl(url);
  }

  async analyze(input: DetectionInput): Promise<DetectionResult> {
    const result = await this.detector.analyze(input);

    // Emit events if there are listeners
    if (result.isAgent && this.events.has('agent.detected')) {
      const handlers = this.events.get('agent.detected') || [];
      handlers.forEach((handler) => handler(result, input));
    }

    return result;
  }

  on(event: string, handler: EventHandler): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(handler);
  }

  emit(event: string, ...args: unknown[]): void {
    const handlers = this.events.get(event) || [];
    handlers.forEach((handler) => handler(...args));
  }

  async init(): Promise<void> {
    await this.detector.init();
  }
}
